import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search, Trash2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories, catName } from "@/lib/hooks";
import { PageHeader, Empty } from "@/components/common";
import { Field } from "@/components/PropertyDialog";

function FaqCreate() {
  const { t, lang } = usePrefs();
  const cats = useCategories();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", category: "autre", problem: "", solution: "" });
  const save = async () => {
    try { await api.post("/faq", f); qc.invalidateQueries({ queryKey: ["faq"] }); setOpen(false); setF({ title: "", category: "autre", problem: "", solution: "" }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="new-faq-button"><Plus size={16} className="mr-1" />{t("create")}</Button></DialogTrigger>
      <DialogContent className="rounded-sm" data-testid="faq-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("faq")}</DialogTitle></DialogHeader>
        <Field label={t("title")}><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="faq-new-title-input" /></Field>
        <Field label={t("category")}>
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
            <SelectTrigger data-testid="faq-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.trade.map((c) => <SelectItem key={c.key} value={c.key}>{catName(c, lang)}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label={t("problem")}><Textarea value={f.problem} onChange={(e) => setF({ ...f, problem: e.target.value })} data-testid="faq-new-problem-input" /></Field>
        <Field label={t("solution")}><Textarea rows={4} value={f.solution} onChange={(e) => setF({ ...f, solution: e.target.value })} data-testid="faq-new-solution-input" /></Field>
        <Button onClick={save} disabled={!f.title || !f.solution} className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="faq-new-save-button">{t("save")}</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Faq() {
  const { user } = useAuth();
  const { t } = usePrefs();
  const { label } = useCategories();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const { data = [] } = useQuery({ queryKey: ["faq", q], queryFn: () => fetcher(`/faq?q=${encodeURIComponent(q)}`) });
  const staff = ["admin", "supervision", "gestionnaire"].includes(user.role);
  const del = async (id) => { try { await api.delete(`/faq/${id}`); qc.invalidateQueries({ queryKey: ["faq"] }); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="faq-page">
      <PageHeader eyebrow="FAQ" title={t("faq")}>{staff && <FaqCreate />}</PageHeader>
      <div className="relative mb-6 max-w-md">
        <Search size={15} className="absolute left-3 top-3 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="h-10 rounded-sm bg-white pl-9" data-testid="faq-search-input" />
      </div>
      {data.length === 0 && <Empty />}
      <Accordion type="multiple" className="rounded-sm border border-slate-200 bg-white">
        {data.map((f) => (
          <AccordionItem key={f.id} value={f.id} className="px-5" data-testid={`faq-item-${f.id}`}>
            <AccordionTrigger className="text-left hover:no-underline">
              <span><span className="mr-3 text-[10px] font-bold uppercase tracking-widest text-[#996515]">{label(f.category)}</span><span className="font-semibold">{f.title}</span></span>
            </AccordionTrigger>
            <AccordionContent>
              {f.problem && <p className="mb-2 text-sm text-slate-500"><b>{t("problem")} :</b> {f.problem}</p>}
              <p className="text-sm text-slate-800"><b>{t("solution")} :</b> {f.solution}</p>
              <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                <span>{f.author}{f.source_ticket_id ? " · depuis un ticket" : ""}</span>
                {staff && <button onClick={() => del(f.id)} className="hover:text-rose-600" data-testid={`faq-delete-${f.id}`}><Trash2 size={13} /></button>}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
